import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  householdApi,
  householdUsers,
  invites,
  type HouseholdMember,
  type Invite,
  type UserRole,
} from '../api';
import { useAuth } from '../context/AuthContext';

const ROLE_OPTIONS: Array<'admin' | 'member' | 'viewer'> = ['admin', 'member', 'viewer'];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (!user) return null;

  const canManageHousehold = user.role === 'owner' || user.role === 'admin';

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('settings.title')}</h1>
        <p className="text-sm text-slate-600">{t('settings.intro')}</p>
      </div>

      <HouseholdSection canEdit={canManageHousehold} />
      <MembersSection currentUser={user} />
      {canManageHousehold && <InvitesSection />}
    </div>
  );
}

function HouseholdSection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: household } = useQuery({
    queryKey: ['household'],
    queryFn: householdApi.get,
  });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: () => householdApi.update({ name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['household'] });
      setEditing(false);
      setError(null);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  function startEdit() {
    setName(household?.name ?? '');
    setEditing(true);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    updateMutation.mutate();
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('settings.household.title')}</h2>
        {canEdit && !editing && (
          <button
            onClick={startEdit}
            className="text-sm text-slate-600 hover:text-slate-900 hover:underline"
          >
            {t('common.edit')}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm mb-3">{error}</div>
      )}

      {editing ? (
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              {t('settings.household.name')}
            </span>
            <input
              type="text"
              required
              maxLength={200}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-slate-900 text-white rounded px-4 py-1.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {updateMutation.isPending ? t('common.working') : t('common.save')}
            </button>
          </div>
        </form>
      ) : (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-slate-500">{t('settings.household.name')}</dt>
            <dd className="text-slate-900 font-medium">{household?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">{t('settings.household.plan')}</dt>
            <dd className="text-slate-900">{household?.planCode ?? '—'}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function MembersSection({ currentUser }: { currentUser: { id: number; role: UserRole } }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: members = [] } = useQuery({
    queryKey: ['household-users'],
    queryFn: householdUsers.list,
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => householdUsers.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['household-users'] }),
  });

  const changeRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: 'admin' | 'member' | 'viewer' }) =>
      householdUsers.changeRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['household-users'] }),
  });

  const isOwner = currentUser.role === 'owner';
  const isAdmin = currentUser.role === 'admin';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">{t('settings.members.title')}</h2>
      <p className="text-sm text-slate-600 mb-4">{t('settings.members.intro')}</p>

      <div className="border border-slate-200 rounded divide-y divide-slate-200">
        {members.length === 0 ? (
          <div className="p-4 text-sm text-slate-500 text-center">
            {t('settings.members.empty')}
          </div>
        ) : (
          members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              isSelf={m.id === currentUser.id}
              canChangeRole={isOwner && m.role !== 'owner'}
              canRemove={
                (isOwner || (isAdmin && m.role === 'member') || (isAdmin && m.role === 'viewer')) &&
                m.role !== 'owner' &&
                m.id !== currentUser.id
              }
              onChangeRole={(role) => changeRoleMutation.mutate({ id: m.id, role })}
              onRemove={() => removeMutation.mutate(m.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MemberRow({
  member,
  isSelf,
  canChangeRole,
  canRemove,
  onChangeRole,
  onRemove,
}: {
  member: HouseholdMember;
  isSelf: boolean;
  canChangeRole: boolean;
  canRemove: boolean;
  onChangeRole: (role: 'admin' | 'member' | 'viewer') => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="p-3 flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900 truncate">{member.name}</span>
          {isSelf && <span className="text-xs text-slate-400">— {t('settings.members.you')}</span>}
          {!member.emailVerifiedAt && (
            <span className="text-xs text-amber-700 bg-amber-50 px-1.5 rounded">
              {t('settings.members.unverified')}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate">{member.email}</div>
      </div>
      <div className="flex items-center gap-2">
        {canChangeRole ? (
          <select
            value={member.role}
            onChange={(e) =>
              onChangeRole(e.target.value as 'admin' | 'member' | 'viewer')
            }
            className="text-sm rounded border border-slate-300 px-2 py-1"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {t(`role.${r}`)}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-slate-600 px-2 py-1">{t(`role.${member.role}`)}</span>
        )}
        {canRemove && (
          <button
            onClick={() => {
              if (confirm(t('settings.members.confirmRemove', { name: member.name }))) {
                onRemove();
              }
            }}
            className="text-sm text-red-600 hover:text-red-800 hover:underline"
          >
            {t('common.delete')}
          </button>
        )}
      </div>
    </div>
  );
}

function InvitesSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: list = [] } = useQuery({
    queryKey: ['invites'],
    queryFn: invites.list,
  });

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => invites.create({ email, role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] });
      setEmail('');
      setRole('member');
      setShowForm(false);
      setError(null);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => invites.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('settings.invites.title')}</h2>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-sm bg-slate-900 text-white rounded px-3 py-1.5 hover:bg-slate-800 font-medium"
          >
            {t('settings.invites.new')}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm mb-3">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-slate-50 rounded p-3 mb-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">{t('field.email')}</span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                {t('settings.invites.role')}
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'member' | 'viewer')}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`role.${r}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEmail('');
                setRole('member');
              }}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-slate-900 text-white rounded px-3 py-1.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {createMutation.isPending ? t('common.working') : t('settings.invites.send')}
            </button>
          </div>
        </form>
      )}

      {list.length === 0 ? (
        <p className="text-sm text-slate-500">{t('settings.invites.empty')}</p>
      ) : (
        <ul className="border border-slate-200 rounded divide-y divide-slate-200">
          {list.map((inv) => (
            <InviteRow key={inv.id} invite={inv} onRemove={() => removeMutation.mutate(inv.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function InviteRow({ invite, onRemove }: { invite: Invite; onRemove: () => void }) {
  const { t } = useTranslation();
  const expires = new Date(invite.expiresAt);
  const expired = expires.getTime() < Date.now();
  return (
    <li className="p-3 flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{invite.email}</span>
          <span className="text-xs text-slate-500">— {t(`role.${invite.role}`)}</span>
          {expired && (
            <span className="text-xs text-red-700 bg-red-50 px-1.5 rounded">
              {t('settings.invites.expired')}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {t('settings.invites.expiresAt', { date: expires.toLocaleDateString('da-DK') })}
        </div>
      </div>
      <button
        onClick={() => {
          if (confirm(t('settings.invites.confirmRemove'))) onRemove();
        }}
        className="text-sm text-red-600 hover:text-red-800 hover:underline"
      >
        {t('common.delete')}
      </button>
    </li>
  );
}
